import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/useAuth';
import { useManagedLocations } from '../../auth/useManagedLocations';
import { NominationScope, SuccessNominationRecord } from '../nomination.types';
import { fetchClientNominations, subscribeToNominations } from '../services/nominationService';
import { describeNominationError } from '../utils/nominationErrors';

/** Derives the Firestore read scope for the signed-in user (null until auth resolves). */
export function useNominationScope(): NominationScope | null {
  const { user, userRole } = useAuth();
  const assignedLocations = useManagedLocations();

  return useMemo(() => {
    if (!user || !userRole) return null;
    return {
      role: userRole,
      uid: user.uid,
      assignedLocations,
    };
  }, [user, userRole, assignedLocations]);
}

interface UseNominationsResult {
  nominations: SuccessNominationRecord[];
  isLoading: boolean;
  error: string | null;
  retry: () => void;
}

/** Realtime, role-scoped nominations feed for the review page. */
export function useNominations(scope: NominationScope | null): UseNominationsResult {
  const [nominations, setNominations] = useState<SuccessNominationRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!scope) return undefined;

    setIsLoading(true);
    setError(null);

    const unsubscribe = subscribeToNominations(
      scope,
      records => {
        setNominations(records);
        setIsLoading(false);
      },
      subscriptionError => {
        setError(describeNominationError(subscriptionError, scope));
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [scope, attempt]);

  const retry = useCallback(() => setAttempt(current => current + 1), []);

  return { nominations, isLoading, error, retry };
}

interface UseClientNominationsResult {
  nominations: SuccessNominationRecord[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/** Nominations for a single client (scoped per role), used by the Client Success tab. */
export function useClientNominations(clientId: string | undefined, scope: NominationScope | null): UseClientNominationsResult {
  const [nominations, setNominations] = useState<SuccessNominationRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!clientId || !scope) {
      setNominations([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const records = await fetchClientNominations(clientId, scope);
      setNominations(records);
    } catch (fetchError) {
      console.error(
        '[success-nominations] failed to load client nominations:',
        describeNominationError(fetchError, scope),
        fetchError
      );
      setError('Unable to load nominations for this client.');
    } finally {
      setIsLoading(false);
    }
  }, [clientId, scope]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      await load();
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [load]);

  return { nominations, isLoading, error, refresh: load };
}
